// Page "Équipe" (Lot 6A + 6B) — liste des membres du tenant courant +
// section d'invitations (Lot 6B).
//
// Deux sections, empilées :
// 1) Membres actuels : dropdown de rôle inline + bouton Retirer
// 2) {t("membersPage.pendingTitle")} (+ formulaire pour en émettre une)
//
// Gardes côté UI :
// - Si l'utilisateur n'est pas ADMIN, on affiche un message au lieu de la liste.
// - On désactive le bouton "Retirer" et le dropdown pour soi-même si on est
//   seul admin, pour éviter un appel qui serait refusé en 409.
//
// Le flow d'invitation est hybride côté backend : si l'email correspond à un
// user existant, il est ajouté direct ; sinon, une invitation par email est
// créée. L'UI reflète ces deux modes via `InviteMemberResponse.mode`.

import { FormEvent, useEffect, useMemo, useState } from "react";

import { ApiError } from "../api/client";
import {
  invitationsApi,
  type InvitationPublic,
  type InviteMemberResponse,
} from "../api/invitations";
import { membersApi, type MemberPublic } from "../api/members";
import type { UserRole } from "../api/types";
import { useTranslation } from "react-i18next";
import { AppShell } from "../components/AppShell";
import { Modal } from "../components/Modal";
import { useAuthStore } from "../stores/auth";

function getRoleOptions(t: (k: string) => string) {
  return [
    { value: "admin" as UserRole, label: t("membersPage.roleAdmin"), hint: t("membersPage.hintAdmin") },
    { value: "comptable" as UserRole, label: t("membersPage.roleComptable"), hint: t("membersPage.hintComptable") },
    { value: "viewer" as UserRole, label: t("membersPage.roleViewer"), hint: t("membersPage.hintViewer") },
  ];
}

function roleLabel(role: UserRole, t: (k: string) => string): string {
  return getRoleOptions(t).find((o) => o.value === role)?.label ?? role;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatLastLogin(iso: string | null): string {
  if (!iso) return "Jamais";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatExpiry(iso: string, t: (k: string) => string): string {
  const now = Date.now();
  const expiresAt = new Date(iso).getTime();
  const diffMs = expiresAt - now;
  if (diffMs <= 0) return t("membersPage.expired");
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.ceil(diffMs / (1000 * 60 * 60));
  if (days >= 1) return t("membersPage.expiresInDays").replace("{n}", String(days));
  if (hours >= 1) return t("membersPage.expiresInHours").replace("{n}", String(hours));
  return t("membersPage.expiresSoon");
}

export function MembersPage() {
  const { t } = useTranslation();
  const ROLE_OPTIONS = getRoleOptions(t);
  const currentUser = useAuthStore((s) => s.user);
  const currentRole = useAuthStore((s) => s.currentRole);
  const isAdmin = currentRole === "admin";

  const [members, setMembers] = useState<MemberPublic[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});

  const [invitations, setInvitations] = useState<InvitationPublic[] | null>(
    null,
  );
  const [invitationsError, setInvitationsError] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<Record<string, string>>({});
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteBanner, setInviteBanner] = useState<string | null>(null);

  async function reloadMembers() {
    setLoadError(null);
    try {
      const data = await membersApi.list();
      setMembers(data);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.detail
          : err instanceof Error
            ? err.message
            : "Erreur de chargement";
      setLoadError(msg);
      setMembers([]);
    }
  }

  async function reloadInvitations() {
    setInvitationsError(null);
    try {
      const data = await invitationsApi.list();
      // On ne garde que les invitations pending — accepted/revoked sont
      // historiques et polluent l'UI admin. Le backend renvoie tout pour
      // permettre un journal ultérieur, mais la page reste focalisée.
      setInvitations(data.filter((i) => i.status === "pending"));
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.detail
          : err instanceof Error
            ? err.message
            : "Erreur de chargement";
      setInvitationsError(msg);
      setInvitations([]);
    }
  }

  useEffect(() => {
    if (isAdmin) {
      void reloadMembers();
      void reloadInvitations();
    }
  }, [isAdmin]);

  const adminCount = useMemo(
    () => (members ?? []).filter((m) => m.role === "admin").length,
    [members],
  );

  function setError(userId: string, msg: string | null) {
    setRowError((prev) => {
      const next = { ...prev };
      if (msg) next[userId] = msg;
      else delete next[userId];
      return next;
    });
  }

  async function onChangeRole(member: MemberPublic, newRole: UserRole) {
    if (member.role === newRole) return;
    setError(member.user_id, null);

    // Optimiste : on flippe localement, rollback si erreur.
    const prev = members;
    setMembers(
      (prev ?? []).map((m) =>
        m.user_id === member.user_id ? { ...m, role: newRole } : m,
      ),
    );
    try {
      await membersApi.updateRole(member.user_id, { role: newRole });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.detail
          : err instanceof Error
            ? err.message
            : "Erreur lors du changement de rôle";
      setError(member.user_id, msg);
      setMembers(prev);
    }
  }

  async function onRemove(member: MemberPublic) {
    const confirmed = globalThis.confirm(
      `Retirer ${member.full_name} (${member.email}) du tenant ?`,
    );
    if (!confirmed) return;
    setError(member.user_id, null);

    try {
      await membersApi.remove(member.user_id);
      setMembers((prev) =>
        (prev ?? []).filter((m) => m.user_id !== member.user_id),
      );
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.detail
          : err instanceof Error
            ? err.message
            : "Erreur lors du retrait";
      setError(member.user_id, msg);
    }
  }

  async function onRevokeInvitation(invitation: InvitationPublic) {
    const confirmed = globalThis.confirm(
      t("membersPage.revokeConfirm").replace("{email}", invitation.email),
    );
    if (!confirmed) return;
    setRevokeError((prev) => {
      const next = { ...prev };
      delete next[invitation.id];
      return next;
    });
    try {
      await invitationsApi.revoke(invitation.id);
      setInvitations((prev) =>
        (prev ?? []).filter((i) => i.id !== invitation.id),
      );
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.detail
          : err instanceof Error
            ? err.message
            : "Erreur lors de la révocation";
      setRevokeError((prev) => ({ ...prev, [invitation.id]: msg }));
    }
  }

  function onInvitationSent(result: InviteMemberResponse) {
    setInviteOpen(false);
    if (result.mode === "direct") {
      setInviteBanner(
        `${result.member.full_name} (${result.member.email}) a été ajouté comme ${roleLabel(result.member.role, t).toLowerCase()}.`,
      );
      // Recharge la liste des membres pour afficher le nouvel arrivant.
      void reloadMembers();
    } else {
      setInviteBanner(
        t("membersPage.inviteSent").replace("{email}", result.invitation.email).replace("{date}", formatDate(result.invitation.expires_at)),
      );
      void reloadInvitations();
    }
  }

  if (!isAdmin) {
    return (
      <AppShell>
        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-900">{t("membersPage.title")}</h1>
        </header>
        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-md">
          Seuls les administrateurs peuvent gérer les membres du tenant.
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{t("membersPage.title")}</h1>
          <p className="text-sm text-slate-500">{t("membersPage.subtitle")}</p>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            setInviteBanner(null);
            setInviteOpen(true);
          }}
        >
          {t("membersPage.invite")}
        </button>
      </header>

      {inviteBanner && (
        <div
          className="mb-4 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-3"
          role="status"
        >
          {inviteBanner}
        </div>
      )}

      {loadError && (
        <div
          className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3"
          role="alert"
        >
          {loadError}
        </div>
      )}

      {members === null ? (
        <p className="text-sm text-slate-500">{t("common.loading")}</p>
      ) : members.length === 0 ? (
        <p className="text-sm text-slate-500">Aucun membre.</p>
      ) : (
        <section className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">{t("membersPage.colName")}</th>
                <th className="text-left px-4 py-2">{t("common.email")}</th>
                <th className="text-left px-4 py-2">{t("membersPage.colRole")}</th>
                <th className="text-left px-4 py-2">{t("membersPage.colJoined")}</th>
                <th className="text-left px-4 py-2">{t("membersPage.colLastLogin")}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {members.map((m) => {
                const isSelf = currentUser?.id === m.user_id;
                const lastAdmin = m.role === "admin" && adminCount <= 1;
                const err = rowError[m.user_id];
                return (
                  <tr key={m.user_id}>
                    <td className="px-4 py-3 text-slate-800">
                      {m.full_name}
                      {isSelf && (
                        <span className="ml-2 text-xs text-slate-400">
                          {t("membersPage.you")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{m.email}</td>
                    <td className="px-4 py-3">
                      <select
                        aria-label={`Rôle de ${m.full_name}`}
                        value={m.role}
                        disabled={lastAdmin}
                        title={
                          lastAdmin
                            ? "Dernier administrateur — promouvoir d'abord un autre membre"
                            : undefined
                        }
                        onChange={(e) =>
                          void onChangeRole(m, e.target.value as UserRole)
                        }
                        className="block rounded-md border border-slate-300 text-sm px-2 py-1 disabled:bg-slate-100 disabled:text-slate-500"
                      >
                        {ROLE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {err && (
                        <p className="text-xs text-red-600 mt-1">{err}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {formatDate(m.joined_at)}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {formatLastLogin(m.last_login_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => void onRemove(m)}
                        disabled={lastAdmin}
                        title={
                          lastAdmin
                            ? "Dernier administrateur — impossible à retirer"
                            : undefined
                        }
                        className={[
                          "text-sm font-medium",
                          lastAdmin
                            ? "text-slate-300 cursor-not-allowed"
                            : "text-red-600 hover:text-red-800",
                        ].join(" ")}
                      >
                        {t("membersPage.remove")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {/* -- Invitations en attente ----------------------------------------- */}
      <section className="mt-10">
        <header className="mb-3">
          <h2 className="text-lg font-semibold text-slate-900">
            {t("membersPage.pendingTitle")}
          </h2>
          <p className="text-sm text-slate-500">
            {t("membersPage.pendingDesc")}
          </p>
        </header>

        {invitationsError && (
          <div
            className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3"
            role="alert"
          >
            {invitationsError}
          </div>
        )}

        {invitations === null ? (
          <p className="text-sm text-slate-500">{t("common.loading")}</p>
        ) : invitations.length === 0 ? (
          <p className="text-sm text-slate-500">{t("membersPage.noPending")}</p>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2">{t("membersPage.proposedRole")}</th>
                  <th className="text-left px-4 py-2">{t("membersPage.colSent")}</th>
                  <th className="text-left px-4 py-2">{t("membersPage.colExpiry")}</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invitations.map((inv) => {
                  const err = revokeError[inv.id];
                  return (
                    <tr key={inv.id}>
                      <td className="px-4 py-3 text-slate-800">{inv.email}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {roleLabel(inv.role, t)}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {formatDate(inv.created_at)}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {formatExpiry(inv.expires_at, t)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => void onRevokeInvitation(inv)}
                          className="text-sm font-medium text-red-600 hover:text-red-800"
                        >
                          {t("common.cancel")}
                        </button>
                        {err && (
                          <p className="text-xs text-red-600 mt-1">{err}</p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <InviteMemberModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSent={onInvitationSent}
      />
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Modale d'invitation
// ---------------------------------------------------------------------------

type InviteMemberModalProps = {
  open: boolean;
  onClose: () => void;
  onSent: (result: InviteMemberResponse) => void;
};

function InviteMemberModal({ open, onClose, onSent }: InviteMemberModalProps) {
  const { t } = useTranslation();
  const ROLE_OPTIONS = getRoleOptions(t);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<UserRole>("comptable");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setRole("comptable");
      setError(null);
      setPending(false);
    }
  }, [open]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const result = await invitationsApi.inviteMember({ email, role });
      onSent(result);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.detail
          : err instanceof Error
            ? err.message
            : "Erreur lors de l'invitation";
      setError(msg);
    } finally {
      setPending(false);
    }
  }

  return (
    <Modal open={open} title={t("membersPage.invite")} onClose={onClose} size="md">
      <form onSubmit={onSubmit} className="space-y-4" aria-label="invite-form">
        <div>
          <label
            htmlFor="invite_email"
            className="block text-sm font-medium text-slate-700 mb-1"
          >
            {t("common.email")}
          </label>
          <input
            id="invite_email"
            type="email"
            required
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
            placeholder="prenom.nom@exemple.fr"
          />
          <p className="text-xs text-slate-400 mt-1">
            Si la personne a déjà un compte HInvoice, elle rejoint le tenant
            directement. Sinon, un email d'invitation lui est envoyé.
          </p>
        </div>
        <div>
          <label
            htmlFor="invite_role"
            className="block text-sm font-medium text-slate-700 mb-1"
          >
            Rôle
          </label>
          <select
            id="invite_role"
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
            className="input"
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label} — {opt.hint.toLowerCase()}
              </option>
            ))}
          </select>
        </div>
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            {t("common.cancel")}
          </button>
          <button type="submit" disabled={pending} className="btn-primary">
            {pending ? t("common.saving") : t("membersPage.invite")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
